import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GoogleSheetProvider,
  useGoogleSheet,
} from '../GoogleSheetsIntegration/GoogleSheetsContext';
import { NodeProps } from '../../util/Types/NodeProps';
import {
  GoogleDocsProvider,
  PushDataOption,
  useGoogleDocs,
} from '../GoogleDocsIntegration/GoogleDocsContext';
import PushGoogleSheetsData from '../GoogleSheetsIntegration/PushGoogleSheetsData';
import { Box, Button, MenuItem, Select, Tooltip } from '@mui/material';
import PushGoogleDocsData from '../GoogleDocsIntegration/PushGoogleDocsData';
import Node from '../UI/Node/Node';
import NoConnection from '../PullDataNode/NoConnection/NoConnection';
import TagInput from '../Test/TagInput';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { useEdges } from 'reactflow';
import useFlowsStore from '../../store/storeFlows';
import Label from '../UI/Label/Label';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { arrayDataToText } from '../../util/dataToText';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { getAPIErrorMessage } from '../../helpers/helpers';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import { Info } from '@phosphor-icons/react';
import { useAuthStore } from '../../store/storeAuth';
import { LocalStorageService } from '../../service/LocalStorageService';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import { isActiveEdge } from '../../util/findActiveHandle';
import { FlowService } from '../../service/FlowService';

enum GoogleService {
  GoogleSheets = 'Google Sheets',
  GoogleDocs = 'Google Docs',
}

const PushDataNode: React.FC<{ nodeId: string; type: string; data: any }> = ({
  nodeId: id,
  data,
  type,
}) => {
  const {
    checkingConnection,
    isConnected,
    disconnectHandler,
    pushDataHandler,
    pushDataToNewSheetHandler,
    updateSelectedSpreadSheet,
    loading,
    selectedWorkSheet,
    refreshSpreadSheets,
    updateSelectedWorkSheet,
    selectedSpreadSheet,
    spreadSheets,
    setIsConnected,
    isRefreshing,
  } = useGoogleSheet();
  const setNotification = useNotificationStore(state => state.setNotification);
  const {
    pushDataHandler: pushDataHandlerDocs,
    selectedDoc,
    updateSelectedDoc,
    refreshDocsHandler,
    docs,
    createAndPushDataToNewDoc,
  } = useGoogleDocs();
  const [formData, setFormData] = useState({
    textContent: '',
    text: '',
    selectedGoogleService: GoogleService.GoogleSheets,
    spreadsheet: 'none',
    sheets: 'new',
    name: '',
    selectedPushDataOption: PushDataOption.END_OF_DOC,
    specifiedPage: '',
    docs: 'none',
    newDocumentName: '',
  });

  const { setNodeState, setSaving, setSchema, flowId } = useFlowsStore(
    state => state,
  );

  const edges = useEdges();

  const authUser = useAuthStore(state => state);

  const [isDisabled, setIsDisabled] = useState(true);
  const [initial, setInitial] = useState({
    docs: true,
    sheets: true,
  });

  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChanging, setWhoIsChanging] = useState({
    value: '',
    name: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasToExecute, setHasToExecute] = useState(true);

  const setValue = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    setFormData({
      text: data.text || '',
      textContent: data.textContent || '',
      selectedGoogleService:
        data.selectedGoogleService || GoogleService.GoogleSheets,
      spreadsheet: data.spreadsheet || 'none',
      sheets: data.sheets !== undefined ? data.sheets : 'new',
      name: data.name || '',
      selectedPushDataOption:
        data.selectedPushDataOption || PushDataOption.END_OF_DOC,
      specifiedPage: data.specifiedPage || '',
      docs: data.docs || '',
      newDocumentName: data.newDocumentName || '',
    });
  }, [data]);

  const debounceTimeoutRef = useRef<any>(null);

  // useEffect(() => {
  //   const googleSheetsAccessToken = LocalStorageService.getItem(
  //     'googleSheetsAccessToken'
  //   );
  //   if (googleSheetsAccessToken) {
  //     setIsConnected(true);
  //   }
  //   else {
  //     setIsConnected(false)
  //   }
  // }, []);

  useEffect(() => {
    const updateDisabledAuth = async () => {
      if (!authUser?.user) return;

      try {
        const { data } = await FlowService.getFlow(flowId);
        const { creatorId, spaceId } = data;

        if (!spaceId) {
          return setIsDisabled(false);
        }

        if (authUser.user.id !== creatorId) {
          setIsDisabled(true);
        } else {
          setIsDisabled(false);
        }
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    };

    updateDisabledAuth();
  }, [authUser, flowId]);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          onSubmit();
        }, 1000); // Adjust the debounce delay as needed (e.g., 300 milliseconds)
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [data?.flowTrigger, hasToExecute, formData, data?.canceled]);

  const onSubmit = async () => {
    setIsLoading(true);

    try {
      if (!selectedSpreadSheet || !selectedWorkSheet || !selectedDoc) {
        updateSelectedDoc(formData.docs);
        updateSelectedSpreadSheet(formData.spreadsheet);
        updateSelectedWorkSheet(formData.sheets as any);
      }
      let text = formData.textContent;

      if (data?.textContentData?.length > 0) {
        text = arrayDataToText(data.textContentData);
      }

      if (formData.selectedGoogleService === GoogleService.GoogleSheets) {
        if (formData.sheets === 'new') {
          await pushDataToNewSheetHandler(formData.name, text);
        } else {
          await pushDataHandler(text);
        }
      }
      if (formData.selectedGoogleService === GoogleService.GoogleDocs) {
        if (!selectedDoc) {
          const googleDocName = formData.newDocumentName;
          if (!googleDocName)
            return setNotification({
              type: NotificationType.Error,
              message: 'Document Name is required or select a document',
            });
          await createAndPushDataToNewDoc(
            googleDocName,
            text,
            PushDataOption.BEGINNING_OF_DOC,
            parseInt(formData.specifiedPage),
          );
        } else {
          await pushDataHandlerDocs(
            formData.selectedPushDataOption,
            text,
            parseInt(formData.specifiedPage),
          );
        }
      }

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        setSaving,
        flowTriggerData: checkExecuteTrigger(data, id),
        changeType: 'execute',
        objectCallerData: [
          ...(user?.spaceObjects || []),
          ...(user?.objects || []),
        ],

        objectData: {},
        setSchema,
      });
    } catch (error) {
      let anyError = error as any;
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });

      if (anyError.code === 401) {
        disconnectHandler();
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setHasToExecute(true);
      }, 2500);
    }
  };

  const onChange = (e: { target: { name: string; value: any } }) => {
    setValue(e.target.name, e.target.value);
    setWhoIsChanging({
      name: e.target.name,
      value: e.target.value,
    });
    setIsChangingDirectly(true);
  };

  const onChangeSelect = (e: { target: { name: string; value: any } }) => {
    setValue(e.target.name, e.target.value);
    let object = {};

    if (e.target.name === 'spreadsheet') {
      object = {
        spreadsheet: e.target.value,
        sheets: 'new',
      };
    } else if (e.target.name === 'sheets') {
      object = {
        spreadsheet: selectedSpreadSheet?.id,
        sheets: e.target.value,
      };
    } else if (e.target.name === 'docs') {
      object = {
        selectedDoc: e.target.value,
      };
    }

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: {
        [e.target.name]: e.target.value,
        ...object,
      },
      setSchema,
    });
  };

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
          },
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  useEffect(() => {
    if (
      initial.sheets &&
      !selectedSpreadSheet &&
      !selectedWorkSheet &&
      isConnected &&
      spreadSheets.length > 0
    ) {
      updateSelectedSpreadSheet(formData.spreadsheet);

      setInitial({
        ...initial,
        sheets: false,
      });
    }

    if (initial.docs && !selectedDoc && isConnected && docs.length > 0) {
      updateSelectedDoc(formData.docs);
      setInitial({
        ...initial,
        docs: false,
      });
    }
  }, [
    initial,
    formData,
    selectedSpreadSheet,
    selectedWorkSheet,
    isConnected,
    spreadSheets,
    docs,
    selectedDoc,
  ]);

  const refreshBasedOnService = () => {
    if (formData.selectedGoogleService === GoogleService.GoogleSheets) {
      refreshSpreadSheets();
    }
    if (formData.selectedGoogleService === GoogleService.GoogleDocs) {
      refreshDocsHandler();
    }
  };
  const logOut = () => {
    disconnectHandler();
  };

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  const { user } = useAuthStore(state => state);

  return (
    <Node
      edges={edges}
      btnText="Push Data"
      onSubmit={onSubmit}
      data={data}
      type={type}
      id={id}
      isLoading={isLoading}
    >
      <Box sx={{ cursor: isDisabled ? 'not-allowed' : 'auto' }}>
        <Box display={'flex'}>
          <Box
            p={'16px'}
            borderRight={`1px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`}
            sx={{
              backgroundColor: isDisabled
                ? 'rgba(250, 250, 250, 0.85)'
                : 'white',
              pointerEvents: isDisabled ? 'none' : 'auto',
              cursor: isDisabled ? 'not-allowed' : 'default',
              boxShadow: isDisabled ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none',
              transition: 'all 0.3s ease',
            }}
          >
            <NoConnection
              data={data}
              connection={isConnected}
              nodeType="pushData"
            />

            {isConnected && (
              <>
                <TagInput
                  labelName="Data"
                  handleId={'textContent'}
                  name="textContent"
                  dataConnected={data?.textContentData || []}
                  value={formData?.textContent}
                  onChange={onChange}
                  isActive={isActiveEdge(edges, id, 'textContent', 'target')}
                  // hideTextArea={formData.text ? false : true}
                  isAdvanced={true}
                  // disabled={isActiveEdge(edges, id, 'input', 'target')}
                  findColor={findColor}
                  advancedCollapsed={true}
                  isCollapsed={true}
                  height={'320px'}
                  maxHeight={320}
                  maxGrowHeight={320}
                  nodeId={id}
                  removeIcons={true}
                  nodeLabel={data?.label}
                  placeholder={'Connect data into Google Docs or Sheets'}
                />
              </>
            )}
          </Box>

          <Box p={'16px 24px 16px 16px'}>
            {isConnected && (
              <Box display={'flex'} gap={4}>
                <Label labelName="Select where to push data to" isAdvanced />
                {isDisabled && (
                  <Button sx={{ marginBottom: '8px' }}>
                    No Editing Permission
                  </Button>
                )}
              </Box>
            )}
            <Label
              labelName="Data Type"
              isAdvanced
              labelStyle={{
                fontSize: '14px',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                fontWeight: 400,
              }}
            />

            <Select
              className="nodrag"
              name="selectedGoogleService"
              value={formData.selectedGoogleService}
              onChange={onChangeSelect}
              sx={{
                '& .MuiList-root': {
                  backgroundColor:
                    user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                },
                '& .MuiSvgIcon-root': {
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                },
                borderRadius: '8px',
                border: 'none',
                boxShadow: 'none',
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
                bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .Mui-selected': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                    },
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                },
              }}
            >
              {Object.values(GoogleService).map(googleService => (
                <MenuItem
                  key={googleService}
                  value={googleService}
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  {googleService}
                </MenuItem>
              ))}
            </Select>

            {formData.selectedGoogleService === GoogleService.GoogleSheets ? (
              <PushGoogleSheetsData
                formData={formData}
                onChangeSelect={onChangeSelect}
                onChange={onChange}
                id={id}
                data={data}
                type={type}
              />
            ) : (
              <PushGoogleDocsData
                formData={formData}
                onChangeSelect={onChangeSelect}
                onChange={onChange}
                id={id}
                data={data}
                type={type}
              />
            )}
            {isConnected && !isRefreshing ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginTop: '20px',
                }}
              >
                <Tooltip
                  title="Files are up to date"
                  arrow
                  placement="bottom"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#80C683',
                        color: 'white',
                      },
                    },
                    arrow: {
                      sx: {
                        color: '#80C683',
                      },
                    },
                  }}
                >
                  <Button onClick={refreshBasedOnService}>
                    Refresh Google
                  </Button>
                </Tooltip>
                <IconButtonTooltip
                  title={`
                  The data to pull can be from Google Sheets or Google Docs. New Documents or Spreadsheets can be added by refreshing the Google connection. 
                  `}
                >
                  <Info size={16} />
                </IconButtonTooltip>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginTop: '20px',
                  height: '29px',
                }}
              ></Box>
            )}
          </Box>
        </Box>
      </Box>
    </Node>
  );
};

const PushData: React.FC<NodeProps> = ({ id, data, type }) => {
  return (
    <GoogleSheetProvider>
      <GoogleDocsProvider>
        <PushDataNode nodeId={id} data={data} type={type!} />
      </GoogleDocsProvider>
    </GoogleSheetProvider>
  );
};
export default PushData;
